/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `split_summary` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class SplitSummaryMesg : Mesg {
    public constructor() : super("SplitSummary", Profile.MesgNum.SPLIT_SUMMARY)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** message_index. */
    public var messageIndex: UShort?
        get() = getFieldValue(MESSAGE_INDEX_FIELD_NUM) as? UShort
        set(value) = setFieldValue(MESSAGE_INDEX_FIELD_NUM, value)

    /** timestamp, in s. */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** split_type. */
    public var splitType: SplitType?
        get() = (getFieldValue(SPLIT_TYPE_FIELD_NUM) as? UByte)?.let { SplitType.fromValue(it) }
        set(value) = setFieldValue(SPLIT_TYPE_FIELD_NUM, value?.value)

    /** sport. */
    public var sport: Sport?
        get() = (getFieldValue(SPORT_FIELD_NUM) as? UByte)?.let { Sport.fromValue(it) }
        set(value) = setFieldValue(SPORT_FIELD_NUM, value?.value)

    /** sub_sport. */
    public var subSport: SubSport?
        get() = (getFieldValue(SUB_SPORT_FIELD_NUM) as? UByte)?.let { SubSport.fromValue(it) }
        set(value) = setFieldValue(SUB_SPORT_FIELD_NUM, value?.value)

    /** num_splits. */
    public var numSplits: UShort?
        get() = getFieldValue(NUM_SPLITS_FIELD_NUM) as? UShort
        set(value) = setFieldValue(NUM_SPLITS_FIELD_NUM, value)

    /** total_timer_time, in s. */
    public var totalTimerTime: Double?
        get() = getFieldValue(TOTAL_TIMER_TIME_FIELD_NUM) as? Double
        set(value) = setFieldValue(TOTAL_TIMER_TIME_FIELD_NUM, value)

    /** total_distance, in m. */
    public var totalDistance: Double?
        get() = getFieldValue(TOTAL_DISTANCE_FIELD_NUM) as? Double
        set(value) = setFieldValue(TOTAL_DISTANCE_FIELD_NUM, value)

    /** avg_speed, in m/s. */
    public var avgSpeed: Double?
        get() = getFieldValue(AVG_SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_SPEED_FIELD_NUM, value)

    /** max_speed, in m/s. */
    public var maxSpeed: Double?
        get() = getFieldValue(MAX_SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(MAX_SPEED_FIELD_NUM, value)

    /** total_ascent, in m. */
    public var totalAscent: UShort?
        get() = getFieldValue(TOTAL_ASCENT_FIELD_NUM) as? UShort
        set(value) = setFieldValue(TOTAL_ASCENT_FIELD_NUM, value)

    /** total_descent, in m. */
    public var totalDescent: UShort?
        get() = getFieldValue(TOTAL_DESCENT_FIELD_NUM) as? UShort
        set(value) = setFieldValue(TOTAL_DESCENT_FIELD_NUM, value)

    /** avg_heart_rate, in bpm. */
    public var avgHeartRate: UByte?
        get() = getFieldValue(AVG_HEART_RATE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(AVG_HEART_RATE_FIELD_NUM, value)

    /** max_heart_rate, in bpm. */
    public var maxHeartRate: UByte?
        get() = getFieldValue(MAX_HEART_RATE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(MAX_HEART_RATE_FIELD_NUM, value)

    /** avg_vert_speed, in m/s. */
    public var avgVertSpeed: Double?
        get() = getFieldValue(AVG_VERT_SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_VERT_SPEED_FIELD_NUM, value)

    /** total_calories, in kcal. */
    public var totalCalories: UInt?
        get() = getFieldValue(TOTAL_CALORIES_FIELD_NUM) as? UInt
        set(value) = setFieldValue(TOTAL_CALORIES_FIELD_NUM, value)

    /** avg_cadence, in rpm. */
    public var avgCadence: Double?
        get() = getFieldValue(AVG_CADENCE_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_CADENCE_FIELD_NUM, value)

    /**
     * avg_running_cadence: what avg_cadence means when this message selects it.
     */
    public val avgRunningCadence: Double?
        get() = getFieldValue(AVG_CADENCE_FIELD_NUM, 0, SubFieldSelector.Named("AvgRunningCadence")) as? Double

    public fun setAvgRunningCadence(value: Double?) {
        setFieldValue(AVG_CADENCE_FIELD_NUM, value, 0, SubFieldSelector.Named("AvgRunningCadence"))
    }

    /**
     * avg_swimming_cadence: what avg_cadence means when this message selects it.
     */
    public val avgSwimmingCadence: Double?
        get() = getFieldValue(AVG_CADENCE_FIELD_NUM, 0, SubFieldSelector.Named("AvgSwimmingCadence")) as? Double

    public fun setAvgSwimmingCadence(value: Double?) {
        setFieldValue(AVG_CADENCE_FIELD_NUM, value, 0, SubFieldSelector.Named("AvgSwimmingCadence"))
    }

    /**
     * avg_paddlesport_cadence: what avg_cadence means when this message selects it.
     */
    public val avgPaddlesportCadence: Double?
        get() = getFieldValue(AVG_CADENCE_FIELD_NUM, 0, SubFieldSelector.Named("AvgPaddlesportCadence")) as? Double

    public fun setAvgPaddlesportCadence(value: Double?) {
        setFieldValue(AVG_CADENCE_FIELD_NUM, value, 0, SubFieldSelector.Named("AvgPaddlesportCadence"))
    }

    /**
     * avg_push_cadence: what avg_cadence means when this message selects it.
     *
     * average pushes per minute in all splits of the type
     */
    public val avgPushCadence: UShort?
        get() = getFieldValue(AVG_CADENCE_FIELD_NUM, 0, SubFieldSelector.Named("AvgPushCadence")) as? UShort

    public fun setAvgPushCadence(value: UShort?) {
        setFieldValue(AVG_CADENCE_FIELD_NUM, value, 0, SubFieldSelector.Named("AvgPushCadence"))
    }

    /** max_cadence, in rpm. */
    public var maxCadence: Double?
        get() = getFieldValue(MAX_CADENCE_FIELD_NUM) as? Double
        set(value) = setFieldValue(MAX_CADENCE_FIELD_NUM, value)

    /**
     * max_running_cadence: what max_cadence means when this message selects it.
     */
    public val maxRunningCadence: Double?
        get() = getFieldValue(MAX_CADENCE_FIELD_NUM, 0, SubFieldSelector.Named("MaxRunningCadence")) as? Double

    public fun setMaxRunningCadence(value: Double?) {
        setFieldValue(MAX_CADENCE_FIELD_NUM, value, 0, SubFieldSelector.Named("MaxRunningCadence"))
    }

    /**
     * max_swimming_cadence: what max_cadence means when this message selects it.
     */
    public val maxSwimmingCadence: Double?
        get() = getFieldValue(MAX_CADENCE_FIELD_NUM, 0, SubFieldSelector.Named("MaxSwimmingCadence")) as? Double

    public fun setMaxSwimmingCadence(value: Double?) {
        setFieldValue(MAX_CADENCE_FIELD_NUM, value, 0, SubFieldSelector.Named("MaxSwimmingCadence"))
    }

    /**
     * max_paddlesport_cadence: what max_cadence means when this message selects it.
     */
    public val maxPaddlesportCadence: Double?
        get() = getFieldValue(MAX_CADENCE_FIELD_NUM, 0, SubFieldSelector.Named("MaxPaddlesportCadence")) as? Double

    public fun setMaxPaddlesportCadence(value: Double?) {
        setFieldValue(MAX_CADENCE_FIELD_NUM, value, 0, SubFieldSelector.Named("MaxPaddlesportCadence"))
    }

    /**
     * max_push_cadence: what max_cadence means when this message selects it.
     *
     * max pushes per minute in all splits of the type
     */
    public val maxPushCadence: UShort?
        get() = getFieldValue(MAX_CADENCE_FIELD_NUM, 0, SubFieldSelector.Named("MaxPushCadence")) as? UShort

    public fun setMaxPushCadence(value: UShort?) {
        setFieldValue(MAX_CADENCE_FIELD_NUM, value, 0, SubFieldSelector.Named("MaxPushCadence"))
    }

    /** total_cycles, in cycles. */
    public var totalCycles: UInt?
        get() = getFieldValue(TOTAL_CYCLES_FIELD_NUM) as? UInt
        set(value) = setFieldValue(TOTAL_CYCLES_FIELD_NUM, value)

    /**
     * total_strides: what total_cycles means when this message selects it.
     */
    public val totalStrides: UInt?
        get() = getFieldValue(TOTAL_CYCLES_FIELD_NUM, 0, SubFieldSelector.Named("TotalStrides")) as? UInt

    public fun setTotalStrides(value: UInt?) {
        setFieldValue(TOTAL_CYCLES_FIELD_NUM, value, 0, SubFieldSelector.Named("TotalStrides"))
    }

    /**
     * total_strokes: what total_cycles means when this message selects it.
     */
    public val totalStrokes: UInt?
        get() = getFieldValue(TOTAL_CYCLES_FIELD_NUM, 0, SubFieldSelector.Named("TotalStrokes")) as? UInt

    public fun setTotalStrokes(value: UInt?) {
        setFieldValue(TOTAL_CYCLES_FIELD_NUM, value, 0, SubFieldSelector.Named("TotalStrokes"))
    }

    /**
     * total_reps: what total_cycles means when this message selects it.
     *
     * total reps in all splits
     */
    public val totalReps: UInt?
        get() = getFieldValue(TOTAL_CYCLES_FIELD_NUM, 0, SubFieldSelector.Named("TotalReps")) as? UInt

    public fun setTotalReps(value: UInt?) {
        setFieldValue(TOTAL_CYCLES_FIELD_NUM, value, 0, SubFieldSelector.Named("TotalReps"))
    }

    /**
     * total_pushes: what total_cycles means when this message selects it.
     *
     * total number of pushes in all splits of the type
     */
    public val totalPushes: UInt?
        get() = getFieldValue(TOTAL_CYCLES_FIELD_NUM, 0, SubFieldSelector.Named("TotalPushes")) as? UInt

    public fun setTotalPushes(value: UInt?) {
        setFieldValue(TOTAL_CYCLES_FIELD_NUM, value, 0, SubFieldSelector.Named("TotalPushes"))
    }

    /** avg_temperature, in C. */
    public var avgTemperature: Byte?
        get() = getFieldValue(AVG_TEMPERATURE_FIELD_NUM) as? Byte
        set(value) = setFieldValue(AVG_TEMPERATURE_FIELD_NUM, value)

    /** max_temperature, in C. */
    public var maxTemperature: Byte?
        get() = getFieldValue(MAX_TEMPERATURE_FIELD_NUM) as? Byte
        set(value) = setFieldValue(MAX_TEMPERATURE_FIELD_NUM, value)

    /** min_temperature, in C. */
    public var minTemperature: Byte?
        get() = getFieldValue(MIN_TEMPERATURE_FIELD_NUM) as? Byte
        set(value) = setFieldValue(MIN_TEMPERATURE_FIELD_NUM, value)

    /** avg_vertical_oscillation, in mm. */
    public var avgVerticalOscillation: Double?
        get() = getFieldValue(AVG_VERTICAL_OSCILLATION_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_VERTICAL_OSCILLATION_FIELD_NUM, value)

    /** avg_vertical_ratio, in percent. */
    public var avgVerticalRatio: Double?
        get() = getFieldValue(AVG_VERTICAL_RATIO_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_VERTICAL_RATIO_FIELD_NUM, value)

    /** avg_stance_time, in ms. */
    public var avgStanceTime: Double?
        get() = getFieldValue(AVG_STANCE_TIME_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_STANCE_TIME_FIELD_NUM, value)

    /** avg_stance_time_balance, in percent. */
    public var avgStanceTimeBalance: Double?
        get() = getFieldValue(AVG_STANCE_TIME_BALANCE_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_STANCE_TIME_BALANCE_FIELD_NUM, value)

    /** avg_step_length, in mm. */
    public var avgStepLength: Double?
        get() = getFieldValue(AVG_STEP_LENGTH_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_STEP_LENGTH_FIELD_NUM, value)

    /** avg_power, in watts. */
    public var avgPower: UShort?
        get() = getFieldValue(AVG_POWER_FIELD_NUM) as? UShort
        set(value) = setFieldValue(AVG_POWER_FIELD_NUM, value)

    /** max_power, in watts. */
    public var maxPower: UShort?
        get() = getFieldValue(MAX_POWER_FIELD_NUM) as? UShort
        set(value) = setFieldValue(MAX_POWER_FIELD_NUM, value)

    /** normalized_power, in watts. */
    public var normalizedPower: UShort?
        get() = getFieldValue(NORMALIZED_POWER_FIELD_NUM) as? UShort
        set(value) = setFieldValue(NORMALIZED_POWER_FIELD_NUM, value)

    /** left_right_balance. Total time spent in the standing position */
    public var leftRightBalance: UShort?
        get() = getFieldValue(LEFT_RIGHT_BALANCE_FIELD_NUM) as? UShort
        set(value) = setFieldValue(LEFT_RIGHT_BALANCE_FIELD_NUM, value)

    /** time_standing, in s. Average left platform center offset */
    public var timeStanding: Double?
        get() = getFieldValue(TIME_STANDING_FIELD_NUM) as? Double
        set(value) = setFieldValue(TIME_STANDING_FIELD_NUM, value)

    /** avg_left_pco, in mm. Average right platform center offset */
    public var avgLeftPco: Byte?
        get() = getFieldValue(AVG_LEFT_PCO_FIELD_NUM) as? Byte
        set(value) = setFieldValue(AVG_LEFT_PCO_FIELD_NUM, value)

    /** avg_right_pco, in mm. Average left power phase angles. Data value indexes defined by power_phase_type. */
    public var avgRightPco: Byte?
        get() = getFieldValue(AVG_RIGHT_PCO_FIELD_NUM) as? Byte
        set(value) = setFieldValue(AVG_RIGHT_PCO_FIELD_NUM, value)

    /** Number of values stored in avg_left_power_phase. */
    public val numAvgLeftPowerPhase: Int
        get() = getField(AVG_LEFT_POWER_PHASE_FIELD_NUM)?.numValues ?: 0

    /** avg_left_power_phase, in degrees. Average left power phase peak angles. Data value indexes defined by power_phase_type. */
    public var avgLeftPowerPhase: List<Double?>?
        get() {
            val field = getField(AVG_LEFT_POWER_PHASE_FIELD_NUM) ?: return null
            return List(field.numValues) { getAvgLeftPowerPhase(it) }
        }
        set(value) {
            removeField(AVG_LEFT_POWER_PHASE_FIELD_NUM)
            value?.forEachIndexed { index, element -> setAvgLeftPowerPhase(index, element) }
        }

    public fun getAvgLeftPowerPhase(index: Int): Double? =
        getFieldValue(AVG_LEFT_POWER_PHASE_FIELD_NUM, index) as? Double

    public fun setAvgLeftPowerPhase(index: Int, value: Double?) {
        setFieldValue(AVG_LEFT_POWER_PHASE_FIELD_NUM, value, index)
    }

    /** Number of values stored in avg_left_power_phase_peak. */
    public val numAvgLeftPowerPhasePeak: Int
        get() = getField(AVG_LEFT_POWER_PHASE_PEAK_FIELD_NUM)?.numValues ?: 0

    /** avg_left_power_phase_peak, in degrees. Average right power phase angles. Data value indexes defined by power_phase_type. */
    public var avgLeftPowerPhasePeak: List<Double?>?
        get() {
            val field = getField(AVG_LEFT_POWER_PHASE_PEAK_FIELD_NUM) ?: return null
            return List(field.numValues) { getAvgLeftPowerPhasePeak(it) }
        }
        set(value) {
            removeField(AVG_LEFT_POWER_PHASE_PEAK_FIELD_NUM)
            value?.forEachIndexed { index, element -> setAvgLeftPowerPhasePeak(index, element) }
        }

    public fun getAvgLeftPowerPhasePeak(index: Int): Double? =
        getFieldValue(AVG_LEFT_POWER_PHASE_PEAK_FIELD_NUM, index) as? Double

    public fun setAvgLeftPowerPhasePeak(index: Int, value: Double?) {
        setFieldValue(AVG_LEFT_POWER_PHASE_PEAK_FIELD_NUM, value, index)
    }

    /** Number of values stored in avg_right_power_phase. */
    public val numAvgRightPowerPhase: Int
        get() = getField(AVG_RIGHT_POWER_PHASE_FIELD_NUM)?.numValues ?: 0

    /** avg_right_power_phase, in degrees. Average right power phase peak angles. Data value indexes defined by power_phase_type. */
    public var avgRightPowerPhase: List<Double?>?
        get() {
            val field = getField(AVG_RIGHT_POWER_PHASE_FIELD_NUM) ?: return null
            return List(field.numValues) { getAvgRightPowerPhase(it) }
        }
        set(value) {
            removeField(AVG_RIGHT_POWER_PHASE_FIELD_NUM)
            value?.forEachIndexed { index, element -> setAvgRightPowerPhase(index, element) }
        }

    public fun getAvgRightPowerPhase(index: Int): Double? =
        getFieldValue(AVG_RIGHT_POWER_PHASE_FIELD_NUM, index) as? Double

    public fun setAvgRightPowerPhase(index: Int, value: Double?) {
        setFieldValue(AVG_RIGHT_POWER_PHASE_FIELD_NUM, value, index)
    }

    /** Number of values stored in avg_right_power_phase_peak. */
    public val numAvgRightPowerPhasePeak: Int
        get() = getField(AVG_RIGHT_POWER_PHASE_PEAK_FIELD_NUM)?.numValues ?: 0

    /** avg_right_power_phase_peak, in degrees. Average power by position. Data value indexes defined by rider_position_type. */
    public var avgRightPowerPhasePeak: List<Double?>?
        get() {
            val field = getField(AVG_RIGHT_POWER_PHASE_PEAK_FIELD_NUM) ?: return null
            return List(field.numValues) { getAvgRightPowerPhasePeak(it) }
        }
        set(value) {
            removeField(AVG_RIGHT_POWER_PHASE_PEAK_FIELD_NUM)
            value?.forEachIndexed { index, element -> setAvgRightPowerPhasePeak(index, element) }
        }

    public fun getAvgRightPowerPhasePeak(index: Int): Double? =
        getFieldValue(AVG_RIGHT_POWER_PHASE_PEAK_FIELD_NUM, index) as? Double

    public fun setAvgRightPowerPhasePeak(index: Int, value: Double?) {
        setFieldValue(AVG_RIGHT_POWER_PHASE_PEAK_FIELD_NUM, value, index)
    }

    /** Number of values stored in avg_power_position. */
    public val numAvgPowerPosition: Int
        get() = getField(AVG_POWER_POSITION_FIELD_NUM)?.numValues ?: 0

    /** avg_power_position, in watts. Maximum power by position. Data value indexes defined by rider_position_type. */
    public var avgPowerPosition: List<UShort?>?
        get() {
            val field = getField(AVG_POWER_POSITION_FIELD_NUM) ?: return null
            return List(field.numValues) { getAvgPowerPosition(it) }
        }
        set(value) {
            removeField(AVG_POWER_POSITION_FIELD_NUM)
            value?.forEachIndexed { index, element -> setAvgPowerPosition(index, element) }
        }

    public fun getAvgPowerPosition(index: Int): UShort? =
        getFieldValue(AVG_POWER_POSITION_FIELD_NUM, index) as? UShort

    public fun setAvgPowerPosition(index: Int, value: UShort?) {
        setFieldValue(AVG_POWER_POSITION_FIELD_NUM, value, index)
    }

    /** Number of values stored in max_power_position. */
    public val numMaxPowerPosition: Int
        get() = getField(MAX_POWER_POSITION_FIELD_NUM)?.numValues ?: 0

    /** max_power_position, in watts. */
    public var maxPowerPosition: List<UShort?>?
        get() {
            val field = getField(MAX_POWER_POSITION_FIELD_NUM) ?: return null
            return List(field.numValues) { getMaxPowerPosition(it) }
        }
        set(value) {
            removeField(MAX_POWER_POSITION_FIELD_NUM)
            value?.forEachIndexed { index, element -> setMaxPowerPosition(index, element) }
        }

    public fun getMaxPowerPosition(index: Int): UShort? =
        getFieldValue(MAX_POWER_POSITION_FIELD_NUM, index) as? UShort

    public fun setMaxPowerPosition(index: Int, value: UShort?) {
        setFieldValue(MAX_POWER_POSITION_FIELD_NUM, value, index)
    }

    /** avg_left_torque_effectiveness, in percent. */
    public var avgLeftTorqueEffectiveness: Double?
        get() = getFieldValue(AVG_LEFT_TORQUE_EFFECTIVENESS_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_LEFT_TORQUE_EFFECTIVENESS_FIELD_NUM, value)

    /** avg_right_torque_effectiveness, in percent. */
    public var avgRightTorqueEffectiveness: Double?
        get() = getFieldValue(AVG_RIGHT_TORQUE_EFFECTIVENESS_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_RIGHT_TORQUE_EFFECTIVENESS_FIELD_NUM, value)

    /** avg_left_pedal_smoothness, in percent. */
    public var avgLeftPedalSmoothness: Double?
        get() = getFieldValue(AVG_LEFT_PEDAL_SMOOTHNESS_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_LEFT_PEDAL_SMOOTHNESS_FIELD_NUM, value)

    /** avg_right_pedal_smoothness, in percent. */
    public var avgRightPedalSmoothness: Double?
        get() = getFieldValue(AVG_RIGHT_PEDAL_SMOOTHNESS_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_RIGHT_PEDAL_SMOOTHNESS_FIELD_NUM, value)

    /** avg_combined_pedal_smoothness, in percent. The flow score estimates how long distance wise a cyclist deaccelerates over intervals where deacceleration is unnecessary such as smooth turns or small grade angle intervals. */
    public var avgCombinedPedalSmoothness: Double?
        get() = getFieldValue(AVG_COMBINED_PEDAL_SMOOTHNESS_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_COMBINED_PEDAL_SMOOTHNESS_FIELD_NUM, value)

    /** avg_flow, in Flow. The grit score estimates how challenging a route could be for a cyclist in terms of time spent going over sharp turns or large grade slopes. */
    public var avgFlow: Float?
        get() = getFieldValue(AVG_FLOW_FIELD_NUM) as? Float
        set(value) = setFieldValue(AVG_FLOW_FIELD_NUM, value)

    /** total_grit, in kGrit. */
    public var totalGrit: Float?
        get() = getFieldValue(TOTAL_GRIT_FIELD_NUM) as? Float
        set(value) = setFieldValue(TOTAL_GRIT_FIELD_NUM, value)

    /** swim_stroke. # of active lengths of swim pool */
    public var swimStroke: SwimStroke?
        get() = (getFieldValue(SWIM_STROKE_FIELD_NUM) as? UByte)?.let { SwimStroke.fromValue(it) }
        set(value) = setFieldValue(SWIM_STROKE_FIELD_NUM, value?.value)

    /** num_active_lengths, in lengths. */
    public var numActiveLengths: UShort?
        get() = getFieldValue(NUM_ACTIVE_LENGTHS_FIELD_NUM) as? UShort
        set(value) = setFieldValue(NUM_ACTIVE_LENGTHS_FIELD_NUM, value)

    /** avg_swolf. */
    public var avgSwolf: UShort?
        get() = getFieldValue(AVG_SWOLF_FIELD_NUM) as? UShort
        set(value) = setFieldValue(AVG_SWOLF_FIELD_NUM, value)

    /** avg_stroke_distance, in m. */
    public var avgStrokeDistance: Double?
        get() = getFieldValue(AVG_STROKE_DISTANCE_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_STROKE_DISTANCE_FIELD_NUM, value)

    /** avg_strokes_per_length, in strokes / length. */
    public var avgStrokesPerLength: Double?
        get() = getFieldValue(AVG_STROKES_PER_LENGTH_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_STROKES_PER_LENGTH_FIELD_NUM, value)

    /** avg_ascent, in m. */
    public var avgAscent: UShort?
        get() = getFieldValue(AVG_ASCENT_FIELD_NUM) as? UShort
        set(value) = setFieldValue(AVG_ASCENT_FIELD_NUM, value)

    /** max_ascent, in m. */
    public var maxAscent: UShort?
        get() = getFieldValue(MAX_ASCENT_FIELD_NUM) as? UShort
        set(value) = setFieldValue(MAX_ASCENT_FIELD_NUM, value)

    /** climb_grading_scale. */
    public var climbGradingScale: ClimbGradingScale?
        get() = (getFieldValue(CLIMB_GRADING_SCALE_FIELD_NUM) as? UByte)?.let { ClimbGradingScale.fromValue(it) }
        set(value) = setFieldValue(CLIMB_GRADING_SCALE_FIELD_NUM, value?.value)

    /** climb_max_grade_value. */
    public var climbMaxGradeValue: UInt?
        get() = getFieldValue(CLIMB_MAX_GRADE_VALUE_FIELD_NUM) as? UInt
        set(value) = setFieldValue(CLIMB_MAX_GRADE_VALUE_FIELD_NUM, value)

    /**
     * climb_max_grade_yds: what climb_max_grade_value means when this message selects it.
     */
    public val climbMaxGradeYds: YdsGradingScale?
        get() = (getFieldValue(CLIMB_MAX_GRADE_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("ClimbMaxGradeYds")) as? UByte)?.let { YdsGradingScale.fromValue(it) }

    public fun setClimbMaxGradeYds(value: YdsGradingScale?) {
        setFieldValue(CLIMB_MAX_GRADE_VALUE_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("ClimbMaxGradeYds"))
    }

    /**
     * climb_max_grade_uiaa: what climb_max_grade_value means when this message selects it.
     */
    public val climbMaxGradeUiaa: UiaaGradingScale?
        get() = (getFieldValue(CLIMB_MAX_GRADE_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("ClimbMaxGradeUiaa")) as? UByte)?.let { UiaaGradingScale.fromValue(it) }

    public fun setClimbMaxGradeUiaa(value: UiaaGradingScale?) {
        setFieldValue(CLIMB_MAX_GRADE_VALUE_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("ClimbMaxGradeUiaa"))
    }

    /**
     * climb_max_grade_french: what climb_max_grade_value means when this message selects it.
     */
    public val climbMaxGradeFrench: FrenchGradingScale?
        get() = (getFieldValue(CLIMB_MAX_GRADE_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("ClimbMaxGradeFrench")) as? UByte)?.let { FrenchGradingScale.fromValue(it) }

    public fun setClimbMaxGradeFrench(value: FrenchGradingScale?) {
        setFieldValue(CLIMB_MAX_GRADE_VALUE_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("ClimbMaxGradeFrench"))
    }

    /**
     * climb_max_grade_british_adjectival: what climb_max_grade_value means when this message selects it.
     */
    public val climbMaxGradeBritishAdjectival: BritishAdjectivalGradingScale?
        get() = (getFieldValue(CLIMB_MAX_GRADE_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("ClimbMaxGradeBritishAdjectival")) as? UByte)?.let { BritishAdjectivalGradingScale.fromValue(it) }

    public fun setClimbMaxGradeBritishAdjectival(value: BritishAdjectivalGradingScale?) {
        setFieldValue(CLIMB_MAX_GRADE_VALUE_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("ClimbMaxGradeBritishAdjectival"))
    }

    /**
     * climb_max_grade_british_technical: what climb_max_grade_value means when this message selects it.
     */
    public val climbMaxGradeBritishTechnical: BritishTechnicalGradingScale?
        get() = (getFieldValue(CLIMB_MAX_GRADE_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("ClimbMaxGradeBritishTechnical")) as? UByte)?.let { BritishTechnicalGradingScale.fromValue(it) }

    public fun setClimbMaxGradeBritishTechnical(value: BritishTechnicalGradingScale?) {
        setFieldValue(CLIMB_MAX_GRADE_VALUE_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("ClimbMaxGradeBritishTechnical"))
    }

    /**
     * climb_max_grade_ewbank: what climb_max_grade_value means when this message selects it.
     */
    public val climbMaxGradeEwbank: EwbankGradingScale?
        get() = (getFieldValue(CLIMB_MAX_GRADE_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("ClimbMaxGradeEwbank")) as? UByte)?.let { EwbankGradingScale.fromValue(it) }

    public fun setClimbMaxGradeEwbank(value: EwbankGradingScale?) {
        setFieldValue(CLIMB_MAX_GRADE_VALUE_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("ClimbMaxGradeEwbank"))
    }

    /**
     * climb_max_grade_brazilian: what climb_max_grade_value means when this message selects it.
     */
    public val climbMaxGradeBrazilian: BrazilianGradingScale?
        get() = (getFieldValue(CLIMB_MAX_GRADE_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("ClimbMaxGradeBrazilian")) as? UByte)?.let { BrazilianGradingScale.fromValue(it) }

    public fun setClimbMaxGradeBrazilian(value: BrazilianGradingScale?) {
        setFieldValue(CLIMB_MAX_GRADE_VALUE_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("ClimbMaxGradeBrazilian"))
    }

    /**
     * climb_max_grade_saxon: what climb_max_grade_value means when this message selects it.
     */
    public val climbMaxGradeSaxon: SaxonGradingScale?
        get() = (getFieldValue(CLIMB_MAX_GRADE_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("ClimbMaxGradeSaxon")) as? UByte)?.let { SaxonGradingScale.fromValue(it) }

    public fun setClimbMaxGradeSaxon(value: SaxonGradingScale?) {
        setFieldValue(CLIMB_MAX_GRADE_VALUE_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("ClimbMaxGradeSaxon"))
    }

    /**
     * climb_max_grade_vermin: what climb_max_grade_value means when this message selects it.
     */
    public val climbMaxGradeVermin: VerminGradingScale?
        get() = (getFieldValue(CLIMB_MAX_GRADE_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("ClimbMaxGradeVermin")) as? UByte)?.let { VerminGradingScale.fromValue(it) }

    public fun setClimbMaxGradeVermin(value: VerminGradingScale?) {
        setFieldValue(CLIMB_MAX_GRADE_VALUE_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("ClimbMaxGradeVermin"))
    }

    /**
     * climb_max_grade_font: what climb_max_grade_value means when this message selects it.
     */
    public val climbMaxGradeFont: FontGradingScale?
        get() = (getFieldValue(CLIMB_MAX_GRADE_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("ClimbMaxGradeFont")) as? UByte)?.let { FontGradingScale.fromValue(it) }

    public fun setClimbMaxGradeFont(value: FontGradingScale?) {
        setFieldValue(CLIMB_MAX_GRADE_VALUE_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("ClimbMaxGradeFont"))
    }

    /**
     * climb_max_grade_dankyu: what climb_max_grade_value means when this message selects it.
     */
    public val climbMaxGradeDankyu: DankyuGradingScale?
        get() = (getFieldValue(CLIMB_MAX_GRADE_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("ClimbMaxGradeDankyu")) as? UByte)?.let { DankyuGradingScale.fromValue(it) }

    public fun setClimbMaxGradeDankyu(value: DankyuGradingScale?) {
        setFieldValue(CLIMB_MAX_GRADE_VALUE_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("ClimbMaxGradeDankyu"))
    }

    /** num_falls. */
    public var numFalls: UShort?
        get() = getFieldValue(NUM_FALLS_FIELD_NUM) as? UShort
        set(value) = setFieldValue(NUM_FALLS_FIELD_NUM, value)

    /** num_climbs_attempted. */
    public var numClimbsAttempted: UShort?
        get() = getFieldValue(NUM_CLIMBS_ATTEMPTED_FIELD_NUM) as? UShort
        set(value) = setFieldValue(NUM_CLIMBS_ATTEMPTED_FIELD_NUM, value)

    /** num_climbs_completed. */
    public var numClimbsCompleted: UShort?
        get() = getFieldValue(NUM_CLIMBS_COMPLETED_FIELD_NUM) as? UShort
        set(value) = setFieldValue(NUM_CLIMBS_COMPLETED_FIELD_NUM, value)

    /** max_distance, in m. Distance of the longest split */
    public var maxDistance: Double?
        get() = getFieldValue(MAX_DISTANCE_FIELD_NUM) as? Double
        set(value) = setFieldValue(MAX_DISTANCE_FIELD_NUM, value)

    /** metabolic_calories, in kcal. */
    public var metabolicCalories: UShort?
        get() = getFieldValue(METABOLIC_CALORIES_FIELD_NUM) as? UShort
        set(value) = setFieldValue(METABOLIC_CALORIES_FIELD_NUM, value)

    /** active_time, in s. total active time in all split rounds */
    public var activeTime: Double?
        get() = getFieldValue(ACTIVE_TIME_FIELD_NUM) as? Double
        set(value) = setFieldValue(ACTIVE_TIME_FIELD_NUM, value)

    /** total_fractional_ascent, in m. fractional part of total_ascent */
    public var totalFractionalAscent: Double?
        get() = getFieldValue(TOTAL_FRACTIONAL_ASCENT_FIELD_NUM) as? Double
        set(value) = setFieldValue(TOTAL_FRACTIONAL_ASCENT_FIELD_NUM, value)

    /** total_fractional_descent, in m. fractional part of total_descent */
    public var totalFractionalDescent: Double?
        get() = getFieldValue(TOTAL_FRACTIONAL_DESCENT_FIELD_NUM) as? Double
        set(value) = setFieldValue(TOTAL_FRACTIONAL_DESCENT_FIELD_NUM, value)

    /** avg_fractional_ascent, in m. fractional part of avg_ascent */
    public var avgFractionalAscent: Double?
        get() = getFieldValue(AVG_FRACTIONAL_ASCENT_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_FRACTIONAL_ASCENT_FIELD_NUM, value)

    /** max_fractional_ascent, in m. fractional part of max_descent */
    public var maxFractionalAscent: Double?
        get() = getFieldValue(MAX_FRACTIONAL_ASCENT_FIELD_NUM) as? Double
        set(value) = setFieldValue(MAX_FRACTIONAL_ASCENT_FIELD_NUM, value)

    /** avg_grade_adjusted_speed, in m/s. */
    public var avgGradeAdjustedSpeed: Double?
        get() = getFieldValue(AVG_GRADE_ADJUSTED_SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_GRADE_ADJUSTED_SPEED_FIELD_NUM, value)

    /** avg_stress. */
    public var avgStress: UShort?
        get() = getFieldValue(AVG_STRESS_FIELD_NUM) as? UShort
        set(value) = setFieldValue(AVG_STRESS_FIELD_NUM, value)

    /** total_moving_time, in s. */
    public var totalMovingTime: Double?
        get() = getFieldValue(TOTAL_MOVING_TIME_FIELD_NUM) as? Double
        set(value) = setFieldValue(TOTAL_MOVING_TIME_FIELD_NUM, value)

    /** start_time. */
    public var startTime: Instant?
        get() = (getFieldValue(START_TIME_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(START_TIME_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** min_heart_rate, in bpm. */
    public var minHeartRate: UByte?
        get() = getFieldValue(MIN_HEART_RATE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(MIN_HEART_RATE_FIELD_NUM, value)

    /** avg_stance_time_percent, in percent. */
    public var avgStanceTimePercent: Double?
        get() = getFieldValue(AVG_STANCE_TIME_PERCENT_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_STANCE_TIME_PERCENT_FIELD_NUM, value)

    /** max_total_timer_time, in s. */
    public var maxTotalTimerTime: Double?
        get() = getFieldValue(MAX_TOTAL_TIMER_TIME_FIELD_NUM) as? Double
        set(value) = setFieldValue(MAX_TOTAL_TIMER_TIME_FIELD_NUM, value)

    public companion object {
        public const val MESSAGE_INDEX_FIELD_NUM: UByte = 254u
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val SPLIT_TYPE_FIELD_NUM: UByte = 0u
        public const val SPORT_FIELD_NUM: UByte = 1u
        public const val SUB_SPORT_FIELD_NUM: UByte = 2u
        public const val NUM_SPLITS_FIELD_NUM: UByte = 3u
        public const val TOTAL_TIMER_TIME_FIELD_NUM: UByte = 4u
        public const val TOTAL_DISTANCE_FIELD_NUM: UByte = 5u
        public const val AVG_SPEED_FIELD_NUM: UByte = 6u
        public const val MAX_SPEED_FIELD_NUM: UByte = 7u
        public const val TOTAL_ASCENT_FIELD_NUM: UByte = 8u
        public const val TOTAL_DESCENT_FIELD_NUM: UByte = 9u
        public const val AVG_HEART_RATE_FIELD_NUM: UByte = 10u
        public const val MAX_HEART_RATE_FIELD_NUM: UByte = 11u
        public const val AVG_VERT_SPEED_FIELD_NUM: UByte = 12u
        public const val TOTAL_CALORIES_FIELD_NUM: UByte = 13u
        public const val AVG_CADENCE_FIELD_NUM: UByte = 14u
        public const val MAX_CADENCE_FIELD_NUM: UByte = 15u
        public const val TOTAL_CYCLES_FIELD_NUM: UByte = 16u
        public const val AVG_TEMPERATURE_FIELD_NUM: UByte = 17u
        public const val MAX_TEMPERATURE_FIELD_NUM: UByte = 18u
        public const val MIN_TEMPERATURE_FIELD_NUM: UByte = 19u
        public const val AVG_VERTICAL_OSCILLATION_FIELD_NUM: UByte = 20u
        public const val AVG_VERTICAL_RATIO_FIELD_NUM: UByte = 21u
        public const val AVG_STANCE_TIME_FIELD_NUM: UByte = 22u
        public const val AVG_STANCE_TIME_BALANCE_FIELD_NUM: UByte = 23u
        public const val AVG_STEP_LENGTH_FIELD_NUM: UByte = 24u
        public const val AVG_POWER_FIELD_NUM: UByte = 25u
        public const val MAX_POWER_FIELD_NUM: UByte = 26u
        public const val NORMALIZED_POWER_FIELD_NUM: UByte = 27u
        public const val LEFT_RIGHT_BALANCE_FIELD_NUM: UByte = 28u
        public const val TIME_STANDING_FIELD_NUM: UByte = 29u
        public const val AVG_LEFT_PCO_FIELD_NUM: UByte = 30u
        public const val AVG_RIGHT_PCO_FIELD_NUM: UByte = 31u
        public const val AVG_LEFT_POWER_PHASE_FIELD_NUM: UByte = 32u
        public const val AVG_LEFT_POWER_PHASE_PEAK_FIELD_NUM: UByte = 33u
        public const val AVG_RIGHT_POWER_PHASE_FIELD_NUM: UByte = 34u
        public const val AVG_RIGHT_POWER_PHASE_PEAK_FIELD_NUM: UByte = 35u
        public const val AVG_POWER_POSITION_FIELD_NUM: UByte = 36u
        public const val MAX_POWER_POSITION_FIELD_NUM: UByte = 37u
        public const val AVG_LEFT_TORQUE_EFFECTIVENESS_FIELD_NUM: UByte = 38u
        public const val AVG_RIGHT_TORQUE_EFFECTIVENESS_FIELD_NUM: UByte = 39u
        public const val AVG_LEFT_PEDAL_SMOOTHNESS_FIELD_NUM: UByte = 40u
        public const val AVG_RIGHT_PEDAL_SMOOTHNESS_FIELD_NUM: UByte = 41u
        public const val AVG_COMBINED_PEDAL_SMOOTHNESS_FIELD_NUM: UByte = 42u
        public const val AVG_FLOW_FIELD_NUM: UByte = 43u
        public const val TOTAL_GRIT_FIELD_NUM: UByte = 44u
        public const val SWIM_STROKE_FIELD_NUM: UByte = 47u
        public const val NUM_ACTIVE_LENGTHS_FIELD_NUM: UByte = 48u
        public const val AVG_SWOLF_FIELD_NUM: UByte = 49u
        public const val AVG_STROKE_DISTANCE_FIELD_NUM: UByte = 50u
        public const val AVG_STROKES_PER_LENGTH_FIELD_NUM: UByte = 51u
        public const val AVG_ASCENT_FIELD_NUM: UByte = 52u
        public const val MAX_ASCENT_FIELD_NUM: UByte = 53u
        public const val CLIMB_GRADING_SCALE_FIELD_NUM: UByte = 54u
        public const val CLIMB_MAX_GRADE_VALUE_FIELD_NUM: UByte = 55u
        public const val NUM_FALLS_FIELD_NUM: UByte = 56u
        public const val NUM_CLIMBS_ATTEMPTED_FIELD_NUM: UByte = 58u
        public const val NUM_CLIMBS_COMPLETED_FIELD_NUM: UByte = 59u
        public const val MAX_DISTANCE_FIELD_NUM: UByte = 60u
        public const val METABOLIC_CALORIES_FIELD_NUM: UByte = 64u
        public const val ACTIVE_TIME_FIELD_NUM: UByte = 65u
        public const val TOTAL_FRACTIONAL_ASCENT_FIELD_NUM: UByte = 66u
        public const val TOTAL_FRACTIONAL_DESCENT_FIELD_NUM: UByte = 67u
        public const val AVG_FRACTIONAL_ASCENT_FIELD_NUM: UByte = 68u
        public const val MAX_FRACTIONAL_ASCENT_FIELD_NUM: UByte = 69u
        public const val AVG_GRADE_ADJUSTED_SPEED_FIELD_NUM: UByte = 71u
        public const val AVG_STRESS_FIELD_NUM: UByte = 72u
        public const val TOTAL_MOVING_TIME_FIELD_NUM: UByte = 77u
        public const val START_TIME_FIELD_NUM: UByte = 79u
        public const val MIN_HEART_RATE_FIELD_NUM: UByte = 87u
        public const val AVG_STANCE_TIME_PERCENT_FIELD_NUM: UByte = 91u
        public const val MAX_TOTAL_TIMER_TIME_FIELD_NUM: UByte = 101u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[254] = Field("MessageIndex", 254u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[0] = Field("SplitType", 0u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[1] = Field("Sport", 1u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[2] = Field("SubSport", 2u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[3] = Field("NumSplits", 3u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[4] = Field("TotalTimerTime", 4u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[5] = Field("TotalDistance", 5u, BaseType.UINT32, 100.0, 0.0, "m", false)
            fields[6] = Field("AvgSpeed", 6u, BaseType.UINT32, 1000.0, 0.0, "m/s", false)
            fields[7] = Field("MaxSpeed", 7u, BaseType.UINT32, 1000.0, 0.0, "m/s", false)
            fields[8] = Field("TotalAscent", 8u, BaseType.UINT16, 1.0, 0.0, "m", false)
            fields[9] = Field("TotalDescent", 9u, BaseType.UINT16, 1.0, 0.0, "m", false)
            fields[10] = Field("AvgHeartRate", 10u, BaseType.UINT8, 1.0, 0.0, "bpm", false)
            fields[11] = Field("MaxHeartRate", 11u, BaseType.UINT8, 1.0, 0.0, "bpm", false)
            fields[12] = Field("AvgVertSpeed", 12u, BaseType.SINT32, 1000.0, 0.0, "m/s", false)
            fields[13] = Field("TotalCalories", 13u, BaseType.UINT32, 1.0, 0.0, "kcal", false)
            fields[14] = Field("AvgCadence", 14u, BaseType.UINT16, 128.0, 0.0, "rpm", false)
                .addSubField(
                    SubField("AvgRunningCadence", BaseType.UINT16, 128.0, 0.0, "strides/min").also { subField ->
                        subField.addMap(1u, 1)
                    },
                )
                .addSubField(
                    SubField("AvgSwimmingCadence", BaseType.UINT16, 128.0, 0.0, "strokes/min").also { subField ->
                        subField.addMap(1u, 5)
                    },
                )
                .addSubField(
                    SubField("AvgPaddlesportCadence", BaseType.UINT16, 128.0, 0.0, "strokes/min").also { subField ->
                        subField.addMap(1u, 15)
                        subField.addMap(1u, 37)
                    },
                )
                .addSubField(
                    SubField("AvgPushCadence", BaseType.UINT16, 1.0, 0.0, "pushes/min").also { subField ->
                        subField.addMap(1u, 66)
                        subField.addMap(1u, 65)
                    },
                )
            fields[15] = Field("MaxCadence", 15u, BaseType.UINT16, 128.0, 0.0, "rpm", false)
                .addSubField(
                    SubField("MaxRunningCadence", BaseType.UINT16, 128.0, 0.0, "strides/min").also { subField ->
                        subField.addMap(1u, 1)
                    },
                )
                .addSubField(
                    SubField("MaxSwimmingCadence", BaseType.UINT16, 128.0, 0.0, "strokes/min").also { subField ->
                        subField.addMap(1u, 5)
                    },
                )
                .addSubField(
                    SubField("MaxPaddlesportCadence", BaseType.UINT16, 128.0, 0.0, "strokes/min").also { subField ->
                        subField.addMap(1u, 15)
                        subField.addMap(1u, 37)
                    },
                )
                .addSubField(
                    SubField("MaxPushCadence", BaseType.UINT16, 1.0, 0.0, "pushes/min").also { subField ->
                        subField.addMap(1u, 66)
                        subField.addMap(1u, 65)
                    },
                )
            fields[16] = Field("TotalCycles", 16u, BaseType.UINT32, 1.0, 0.0, "cycles", false)
                .addSubField(
                    SubField("TotalStrides", BaseType.UINT32, 1.0, 0.0, "strides").also { subField ->
                        subField.addMap(1u, 1)
                        subField.addMap(1u, 11)
                    },
                )
                .addSubField(
                    SubField("TotalStrokes", BaseType.UINT32, 1.0, 0.0, "strokes").also { subField ->
                        subField.addMap(1u, 2)
                        subField.addMap(1u, 5)
                        subField.addMap(1u, 15)
                        subField.addMap(1u, 37)
                    },
                )
                .addSubField(
                    SubField("TotalReps", BaseType.UINT32, 1.0, 0.0, "reps").also { subField ->
                        subField.addMap(1u, 62)
                    },
                )
                .addSubField(
                    SubField("TotalPushes", BaseType.UINT32, 1.0, 0.0, "pushes").also { subField ->
                        subField.addMap(1u, 66)
                        subField.addMap(1u, 65)
                    },
                )
            fields[17] = Field("AvgTemperature", 17u, BaseType.SINT8, 1.0, 0.0, "C", false)
            fields[18] = Field("MaxTemperature", 18u, BaseType.SINT8, 1.0, 0.0, "C", false)
            fields[19] = Field("MinTemperature", 19u, BaseType.SINT8, 1.0, 0.0, "C", false)
            fields[20] = Field("AvgVerticalOscillation", 20u, BaseType.UINT16, 10.0, 0.0, "mm", false)
            fields[21] = Field("AvgVerticalRatio", 21u, BaseType.UINT16, 100.0, 0.0, "percent", false)
            fields[22] = Field("AvgStanceTime", 22u, BaseType.UINT16, 10.0, 0.0, "ms", false)
            fields[23] = Field("AvgStanceTimeBalance", 23u, BaseType.UINT16, 100.0, 0.0, "percent", false)
            fields[24] = Field("AvgStepLength", 24u, BaseType.UINT16, 10.0, 0.0, "mm", false)
            fields[25] = Field("AvgPower", 25u, BaseType.UINT16, 1.0, 0.0, "watts", false)
            fields[26] = Field("MaxPower", 26u, BaseType.UINT16, 1.0, 0.0, "watts", false)
            fields[27] = Field("NormalizedPower", 27u, BaseType.UINT16, 1.0, 0.0, "watts", false)
            fields[28] = Field("LeftRightBalance", 28u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[29] = Field("TimeStanding", 29u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[30] = Field("AvgLeftPco", 30u, BaseType.SINT8, 1.0, 0.0, "mm", false)
            fields[31] = Field("AvgRightPco", 31u, BaseType.SINT8, 1.0, 0.0, "mm", false)
            fields[32] = Field("AvgLeftPowerPhase", 32u, BaseType.UINT8, 0.7111111, 0.0, "degrees", false)
            fields[33] = Field("AvgLeftPowerPhasePeak", 33u, BaseType.UINT8, 0.7111111, 0.0, "degrees", false)
            fields[34] = Field("AvgRightPowerPhase", 34u, BaseType.UINT8, 0.7111111, 0.0, "degrees", false)
            fields[35] = Field("AvgRightPowerPhasePeak", 35u, BaseType.UINT8, 0.7111111, 0.0, "degrees", false)
            fields[36] = Field("AvgPowerPosition", 36u, BaseType.UINT16, 1.0, 0.0, "watts", false)
            fields[37] = Field("MaxPowerPosition", 37u, BaseType.UINT16, 1.0, 0.0, "watts", false)
            fields[38] = Field("AvgLeftTorqueEffectiveness", 38u, BaseType.UINT8, 2.0, 0.0, "percent", false)
            fields[39] = Field("AvgRightTorqueEffectiveness", 39u, BaseType.UINT8, 2.0, 0.0, "percent", false)
            fields[40] = Field("AvgLeftPedalSmoothness", 40u, BaseType.UINT8, 2.0, 0.0, "percent", false)
            fields[41] = Field("AvgRightPedalSmoothness", 41u, BaseType.UINT8, 2.0, 0.0, "percent", false)
            fields[42] = Field("AvgCombinedPedalSmoothness", 42u, BaseType.UINT8, 2.0, 0.0, "percent", false)
            fields[43] = Field("AvgFlow", 43u, BaseType.FLOAT32, 1.0, 0.0, "Flow", false)
            fields[44] = Field("TotalGrit", 44u, BaseType.FLOAT32, 1.0, 0.0, "kGrit", false)
            fields[47] = Field("SwimStroke", 47u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[48] = Field("NumActiveLengths", 48u, BaseType.UINT16, 1.0, 0.0, "lengths", false)
            fields[49] = Field("AvgSwolf", 49u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[50] = Field("AvgStrokeDistance", 50u, BaseType.UINT16, 100.0, 0.0, "m", false)
            fields[51] = Field("AvgStrokesPerLength", 51u, BaseType.UINT16, 10.0, 0.0, "strokes / length", false)
            fields[52] = Field("AvgAscent", 52u, BaseType.UINT16, 1.0, 0.0, "m", false)
            fields[53] = Field("MaxAscent", 53u, BaseType.UINT16, 1.0, 0.0, "m", false)
            fields[54] = Field("ClimbGradingScale", 54u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[55] = Field("ClimbMaxGradeValue", 55u, BaseType.UINT32, 1.0, 0.0, "", false)
                .addSubField(
                    SubField("ClimbMaxGradeYds", BaseType.ENUM, 1.0, 0.0, "").also { subField ->
                        subField.addMap(54u, 0)
                    },
                )
                .addSubField(
                    SubField("ClimbMaxGradeUiaa", BaseType.ENUM, 1.0, 0.0, "").also { subField ->
                        subField.addMap(54u, 1)
                    },
                )
                .addSubField(
                    SubField("ClimbMaxGradeFrench", BaseType.ENUM, 1.0, 0.0, "").also { subField ->
                        subField.addMap(54u, 2)
                    },
                )
                .addSubField(
                    SubField("ClimbMaxGradeBritishAdjectival", BaseType.ENUM, 1.0, 0.0, "").also { subField ->
                        subField.addMap(54u, 3)
                    },
                )
                .addSubField(
                    SubField("ClimbMaxGradeBritishTechnical", BaseType.ENUM, 1.0, 0.0, "").also { subField ->
                        subField.addMap(54u, 4)
                    },
                )
                .addSubField(
                    SubField("ClimbMaxGradeEwbank", BaseType.ENUM, 1.0, 0.0, "").also { subField ->
                        subField.addMap(54u, 5)
                    },
                )
                .addSubField(
                    SubField("ClimbMaxGradeBrazilian", BaseType.ENUM, 1.0, 0.0, "").also { subField ->
                        subField.addMap(54u, 6)
                    },
                )
                .addSubField(
                    SubField("ClimbMaxGradeSaxon", BaseType.ENUM, 1.0, 0.0, "").also { subField ->
                        subField.addMap(54u, 7)
                    },
                )
                .addSubField(
                    SubField("ClimbMaxGradeVermin", BaseType.ENUM, 1.0, 0.0, "").also { subField ->
                        subField.addMap(54u, 8)
                    },
                )
                .addSubField(
                    SubField("ClimbMaxGradeFont", BaseType.ENUM, 1.0, 0.0, "").also { subField ->
                        subField.addMap(54u, 9)
                    },
                )
                .addSubField(
                    SubField("ClimbMaxGradeDankyu", BaseType.ENUM, 1.0, 0.0, "").also { subField ->
                        subField.addMap(54u, 10)
                    },
                )
            fields[56] = Field("NumFalls", 56u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[58] = Field("NumClimbsAttempted", 58u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[59] = Field("NumClimbsCompleted", 59u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[60] = Field("MaxDistance", 60u, BaseType.UINT32, 100.0, 0.0, "m", false)
            fields[64] = Field("MetabolicCalories", 64u, BaseType.UINT16, 1.0, 0.0, "kcal", false)
            fields[65] = Field("ActiveTime", 65u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[66] = Field("TotalFractionalAscent", 66u, BaseType.UINT8, 100.0, 0.0, "m", false)
            fields[67] = Field("TotalFractionalDescent", 67u, BaseType.UINT8, 100.0, 0.0, "m", false)
            fields[68] = Field("AvgFractionalAscent", 68u, BaseType.UINT8, 100.0, 0.0, "m", false)
            fields[69] = Field("MaxFractionalAscent", 69u, BaseType.UINT8, 100.0, 0.0, "m", false)
            fields[71] = Field("AvgGradeAdjustedSpeed", 71u, BaseType.UINT32, 1000.0, 0.0, "m/s", false)
            fields[72] = Field("AvgStress", 72u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[77] = Field("TotalMovingTime", 77u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[79] = Field("StartTime", 79u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[87] = Field("MinHeartRate", 87u, BaseType.UINT8, 1.0, 0.0, "bpm", false)
            fields[91] = Field("AvgStanceTimePercent", 91u, BaseType.UINT16, 100.0, 0.0, "percent", false)
            fields[101] = Field("MaxTotalTimerTime", 101u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            return fields
        }
    }
}
